// CB:BEGIN responsive-useDevice
import { useEffect, useMemo, useState } from 'react';

type DeviceInfo = {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  viewportWidth: number;
  viewportHeight: number;
  safeAreaTop: number;
  safeAreaBottom: number;
  // Robust presentation flags
  isMobileLayout: boolean;
  isDesktopLayout: boolean;
};

export function useDevice(): DeviceInfo {
  const getDims = () => ({
    w: (typeof window !== 'undefined' ? window.innerWidth : 1024) || 1024,
    h: (typeof window !== 'undefined' ? window.innerHeight : 768) || 768,
  });

  const [{ w, h }, setDims] = useState(getDims);

  useEffect(() => {
    const onResize = () => setDims(getDims());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const safeAreaTop = useMemo(() => {
    try {
      // Fallback to 0 if env not supported
      const el = document.createElement('div');
      el.style.cssText = 'padding-top: env(safe-area-inset-top); position: absolute; visibility: hidden;';
      document.body.appendChild(el);
      const pt = parseFloat(getComputedStyle(el).paddingTop || '0') || 0;
      document.body.removeChild(el);
      return pt;
    } catch {
      return 0;
    }
  }, [w, h]);

  const safeAreaBottom = useMemo(() => {
    try {
      const el = document.createElement('div');
      el.style.cssText = 'padding-bottom: env(safe-area-inset-bottom); position: absolute; visibility: hidden;';
      document.body.appendChild(el);
      const pb = parseFloat(getComputedStyle(el).paddingBottom || '0') || 0;
      document.body.removeChild(el);
      return pb;
    } catch {
      return 0;
    }
  }, [w, h]);

  const isMobile = w < 640; // simple width hint
  const isTablet = w >= 640 && w < 1024; // width hint
  const isDesktop = w >= 1024; // width hint

  // Robust, UA- and input-aware detection
  const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '') || '';
  const isUaMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|BB10|IEMobile|Opera Mini|KaiOS/i.test(ua);
  const hasTouch = typeof window !== 'undefined' && (('ontouchstart' in window) || (navigator as any)?.maxTouchPoints > 0);
  const coarse = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

  // Only treat as mobile layout when on a mobile UA AND touch/coarse input AND reasonably small viewport
  // Default to desktop in any ambiguity per requirement
  const isMobileLayout = !!(isUaMobile && (hasTouch || coarse) && w <= 900);
  const isDesktopLayout = !isMobileLayout;

  return {
    isMobile,
    isTablet,
    isDesktop,
    viewportWidth: w,
    viewportHeight: h,
    safeAreaTop,
    safeAreaBottom,
    isMobileLayout,
    isDesktopLayout,
  };
}
// CB:END responsive-useDevice

